import { InfraStrukturDB } from 'boot/queryStoreAndCommandBus';
import CryptoJS from 'crypto-js';
import { IDBPDatabase } from 'idb';
import { pack, unpack } from 'msgpackr';

import { QueryStoreRequest } from 'src/util/query/models/QueryStoreRequest';
import { ResultRunTypeBaseRecord } from 'src/util/query/models/ResultRecordBaseType';

class QueryStoreOfflineDatabase {
	private readonly appVersion: string;
	private readonly database: IDBPDatabase<InfraStrukturDB> | null;
	private readonly instanceName: string;
	private readonly user: string;

	constructor(
		database: IDBPDatabase<InfraStrukturDB> | null,
		instanceName: string,
		user: string,
		appVersion: string
	) {
		this.database = database;
		this.instanceName = instanceName;
		this.user = user;
		this.appVersion = appVersion;
	}

	private cachingIsEnabled(): boolean {
		return false;
	}

	public databaseKeyFor<DesiredResponseType>(queryStoreRequest: QueryStoreRequest<DesiredResponseType>): string {
		return `${this.appVersion}-${CryptoJS.SHA256(
			`${this.instanceName}-${this.user}-${JSON.stringify(
				queryStoreRequest.query.name
			)}-${JSON.stringify(queryStoreRequest.query.parameters)}`
		).toString()}`;
	}

	public async load<DesiredResponseType>(queryStoreRequest: QueryStoreRequest<DesiredResponseType>): Promise<
		| {
				baseRecordSuccessResponse?: ResultRunTypeBaseRecord;
				successResponse: DesiredResponseType;
		  }
		| undefined
	> {
		let resultFromDBAsHex = undefined;
		if (this.cachingIsEnabled() && this.database !== null) {
			const databaseKey = this.databaseKeyFor(queryStoreRequest);
			resultFromDBAsHex = (await this.database.get('query', databaseKey)) as {
				data: Uint8Array;
				keys: string[];
			};

			if (resultFromDBAsHex !== undefined) {
				try {
					return unpack(resultFromDBAsHex.data) as {
						baseRecordSuccessResponse?: ResultRunTypeBaseRecord;
						successResponse: DesiredResponseType;
					};
				} catch {
					return undefined;
				}
			}
		}

		return resultFromDBAsHex;
	}

	public removeByAggregateId(aggregateId: string) {
		if (this.cachingIsEnabled() && this.database !== null) {
			const aggregate = JSON.parse(aggregateId) as {
				entryId: number;
				idoId: number;
				instanceId: number;
				moduleName: string;
				sourceContainerId: number;
				targetContainerId: number;
				userId: number;
			};

			this.removeByQueryKey(
				CryptoJS.SHA1(`moduleName=${aggregate.moduleName}#entryId=${aggregate.entryId}`).toString()
			);
		}
	}

	public async removeByDatabaseKey(key: string) {
		if (this.cachingIsEnabled() && this.database !== null) {
			const transaction = this.database.transaction('query', 'readwrite');
			await transaction.objectStore('query').delete(key);

			transaction.commit();
		}
	}

	public removeByQueryKey(key: string) {
		if (this.cachingIsEnabled() && this.database !== null) {
			const transaction = this.database.transaction('query', 'readwrite');
			const objectStore = transaction.objectStore('query');
			void objectStore.delete(IDBKeyRange.only(key));
		}
	}

	public removeByQueryKeys(keys: string[]) {
		if (this.cachingIsEnabled() && this.database !== null) {
			keys.forEach((queryKey) => {
				this.removeByQueryKey(queryKey);
			});
		}
	}

	public async save<DesiredResponseType>(
		queryStoreRequest: QueryStoreRequest<DesiredResponseType>,
		recordToSave: {
			baseRecordSuccessResponse?: ResultRunTypeBaseRecord;
			successResponse: DesiredResponseType;
		}
	): Promise<void> {
		if (
			this.cachingIsEnabled() &&
			this.database !== null &&
			Object.prototype.hasOwnProperty.call(recordToSave.baseRecordSuccessResponse, 'keys') &&
			recordToSave.baseRecordSuccessResponse?.keys !== undefined &&
			recordToSave.baseRecordSuccessResponse?.keys.length > 0
		) {
			const databaseKey = this.databaseKeyFor(queryStoreRequest);
			const transaction = this.database.transaction('query', 'readwrite');
			await transaction.objectStore('query').put(
				{
					data: pack(recordToSave) as Uint8Array,
					keys: recordToSave.baseRecordSuccessResponse?.keys,
				},
				databaseKey
			);

			transaction.commit();
		}
	}
}

export { QueryStoreOfflineDatabase };
